import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { DatePickerWithPresets } from "@/components/ui/date-picker-with-presets.tsx";
import { Input } from "@/components/ui/input.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover.tsx";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command.tsx";
import {
  ReferralType,
  formatReferralType,
  useSupervisedMentorships,
} from "@/lib/api/referrals.ts";
import { useDebounce } from "@/lib/hooks/use-debounce.ts";
import { useRouter, useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import { Plus, SearchIcon, X, Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useState, useMemo } from "react";
import { cn } from "@/lib/utils.ts";
import { CommandLoading } from "cmdk";
import { ReferralForm } from "./referral-form.tsx";

const REFERRAL_TYPE_OPTIONS = Object.values(ReferralType).map((type) => ({
  label: formatReferralType(type),
  value: type,
}));

export function Filters() {
  const {
    search: urlSearch,
    teenId: urlTeenId,
    type: urlType,
    dateFrom: urlDateFrom,
    dateTo: urlDateTo,
  } = useSearch({
    strict: false,
  }) as Record<string, string>;

  const router = useRouter();
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [newReferralOpen, setNewReferralOpen] = useState(false);
  const [teenSelectOpen, setTeenSelectOpen] = useState(false);

  const { data: supervisedData, isPending: isMentorshipsLoading } =
    useSupervisedMentorships(true); // Include deactivated teens for referral filtering

  // Get available teens from supervised mentorships (including deactivated)
  const availableTeens = useMemo(() => {
    if (!supervisedData?.mentorships) return [];
    return supervisedData.mentorships.map((mentorship) => ({
      id: mentorship.mentee_id,
      name: mentorship.mentee_name,
      isDeactivated: mentorship.mentorship_deactivated,
    }));
  }, [supervisedData]);

  // Get selected teen name
  const selectedTeenName = useMemo(() => {
    if (!urlTeenId) return undefined;
    return availableTeens.find((teen) => teen.id === urlTeenId)?.name;
  }, [urlTeenId, availableTeens]);

  const debouncedSearch = useDebounce(search, 500);

  // Update URL when search changes
  useEffect(() => {
    router.navigate({
      search: ((old: any) => ({
        ...old,
        search: debouncedSearch || undefined,
        page: "1",
      })) as any,
    });
  }, [debouncedSearch]);

  // Sync URL params with local state
  useEffect(() => {
    if (urlSearch) {
      setSearch(urlSearch);
    }
    if (urlDateFrom) {
      setDateFrom(new Date(urlDateFrom));
    }
    if (urlDateTo) {
      setDateTo(new Date(urlDateTo));
    }
  }, [urlSearch, urlDateFrom, urlDateTo]);

  const handleDateFromChange = (date: Date | undefined) => {
    setDateFrom(date);
    router.navigate({
      search: ((old: any) => ({
        ...old,
        dateFrom: date ? format(date, "yyyy-MM-dd") : undefined,
        page: "1",
      })) as any,
    });
  };

  const handleDateToChange = (date: Date | undefined) => {
    setDateTo(date);
    router.navigate({
      search: ((old: any) => ({
        ...old,
        dateTo: date ? format(date, "yyyy-MM-dd") : undefined,
        page: "1",
      })) as any,
    });
  };

  const handleTypeChange = (value: string) => {
    router.navigate({
      search: ((old: any) => ({
        ...old,
        type: value === "all" ? undefined : value,
        page: "1",
      })) as any,
    });
  };

  const handleTeenChange = (teenId: string) => {
    router.navigate({
      search: ((old: any) => ({
        ...old,
        teenId: teenId === "all" ? undefined : teenId,
        page: "1",
      })) as any,
    });
    setTeenSelectOpen(false);
  };

  const clearFilters = () => {
    setSearch("");
    setDateFrom(undefined);
    setDateTo(undefined);
    router.navigate({
      search: {
        page: "1",
      } as any,
    });
  };

  const hasActiveFilters =
    urlSearch || urlTeenId || urlType || urlDateFrom || urlDateTo;

  return (
    <div className="mb-2 flex flex-col gap-4 p-2">
      <div className="flex items-center gap-4">
        <div className="flex flex-1 items-center gap-4">
          {/* Date Range Filters */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Date:</span>
            <DatePickerWithPresets
              date={dateFrom}
              onDateChange={handleDateFromChange}
              className="w-fit min-w-[120px]"
              disablePastDates={false}
              showPresets={false}
            />
            <span className="text-muted-foreground text-sm">to</span>
            <DatePickerWithPresets
              date={dateTo}
              onDateChange={handleDateToChange}
              className="w-fit min-w-[120px]"
              disablePastDates={false}
              showPresets={false}
            />
          </div>

          {/* Teen Filter */}
          <div>
            <Popover open={teenSelectOpen} onOpenChange={setTeenSelectOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={teenSelectOpen}
                  className={cn(
                    "w-[200px] justify-between",
                    !urlTeenId && "text-muted-foreground",
                  )}
                  disabled={isMentorshipsLoading}
                >
                  {urlTeenId ? selectedTeenName : "All Teens"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-0">
                <Command>
                  <CommandInput placeholder="Search teen..." />
                  <CommandList>
                    <CommandEmpty>No teens found.</CommandEmpty>
                    {isMentorshipsLoading && (
                      <CommandLoading>Loading teens...</CommandLoading>
                    )}
                    <CommandGroup>
                      <CommandItem
                        value="all"
                        onSelect={() => handleTeenChange("all")}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            !urlTeenId ? "opacity-100" : "opacity-0",
                          )}
                        />
                        All Teens
                      </CommandItem>
                      {availableTeens.map((teen) => (
                        <CommandItem
                          key={teen.id}
                          value={teen.name}
                          onSelect={() => handleTeenChange(teen.id)}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              urlTeenId === teen.id
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                          {teen.name}
                          {teen.isDeactivated && (
                            <span className="text-muted-foreground ml-2 text-xs">
                              (inactive)
                            </span>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Type Filter */}
          <div>
            <Select value={urlType || "all"} onValueChange={handleTypeChange}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {REFERRAL_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-muted-foreground flex items-center gap-1 hover:text-foreground"
            >
              <X className="h-3 w-3" />
              Clear filters
            </Button>
          )}
        </div>

        {/* Search and Add Button */}
        <div className="flex items-center gap-4">
          <Input
            placeholder="Search referrals..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            startIcon={<SearchIcon className="h-5 w-5" />}
            className="w-[300px] rounded-md"
          />
          <Button
            onClick={() => setNewReferralOpen(true)}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            New referral
          </Button>
        </div>
      </div>

      {/* Active Filter Tags */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground text-sm">Active filters:</span>
          {urlSearch && (
            <Badge variant="secondary" className="flex items-center gap-1">
              Search: {urlSearch}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => {
                  setSearch("");
                  router.navigate({
                    search: ((old: any) => ({ ...old, search: undefined })) as any,
                  });
                }}
              />
            </Badge>
          )}
          {urlType && (
            <Badge variant="secondary" className="flex items-center gap-1">
              Type: {formatReferralType(urlType as ReferralType)}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => handleTypeChange("all")}
              />
            </Badge>
          )}
          {urlTeenId && selectedTeenName && (
            <Badge variant="secondary" className="flex items-center gap-1">
              Teen: {selectedTeenName}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => handleTeenChange("all")}
              />
            </Badge>
          )}
          {urlDateFrom && (
            <Badge variant="secondary" className="flex items-center gap-1">
              From: {format(new Date(urlDateFrom), "MMM dd, yyyy")}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => handleDateFromChange(undefined)}
              />
            </Badge>
          )}
          {urlDateTo && (
            <Badge variant="secondary" className="flex items-center gap-1">
              To: {format(new Date(urlDateTo), "MMM dd, yyyy")}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => handleDateToChange(undefined)}
              />
            </Badge>
          )}
        </div>
      )}

      {/* Referral Form Modal */}
      <ReferralForm open={newReferralOpen} onOpenChange={setNewReferralOpen} />
    </div>
  );
}
